import { Middleware } from '@reduxjs/toolkit';
import { saveVaultThunk } from '../thunks/save-vault-thunk';
import { syncMachineService } from '@/services/sync-state';
import { scheduleSync } from '../sync-orchestrator';
import type { AppDispatch } from '../store';

/**
 * Middleware to automatically sync vault data when it changes.
 *
 * When the current vault's data reference changes (i.e. a genuine edit):
 *   1. Persists the new data to localStorage via `saveVaultThunk`.
 *   2. Marks the vault's sync machine dirty (`machine.markDirty()`).
 *   3. Schedules a debounced sync cycle via `scheduleSync`.
 *
 * All sync-slice writes have been removed — the machine state is now the
 * single source of truth for sync status (Phase 2 / Phase 3).
 */
export const syncMiddleware: Middleware = (store) => (next) => (action: unknown) => {
  const prevState = store.getState();
  const prevVault = prevState.vault?.currentVault;

  const result = next(action);

  const nextState = store.getState();
  const nextVault = nextState.vault?.currentVault;
  const vaultMeta = nextVault
    ? nextState.vault?.vaults?.find((v: { info: { id: string } }) => v.info.id === nextVault.info.id)
    : null;
  const isSyncEnabled = vaultMeta?.isSyncEnabled ?? false;
  const isAuthenticated = nextState.vault?.auth?.isAuthenticated === true;

  // Trigger only on vault data mutations, not on infrastructure thunks.
  // The `prevVault !== nextVault` reference check ensures we only fire when
  // data actually changed inside the Redux store (immutable updates).
  if (
    action.type?.startsWith('vault/') &&
    !action.type?.includes('read') &&
    !action.type?.includes('load') &&
    !action.type?.includes('list') &&
    !action.type?.includes('upload') &&
    !action.type?.includes('save') &&
    !action.type?.includes('register') &&
    !action.type?.includes('download') &&
    prevVault &&
    nextVault &&
    prevVault.info.id === nextVault.info.id &&
    prevVault !== nextVault
  ) {
    const vaultId = nextVault.info.id;
    console.log('[SyncMiddleware] Vault data changed:', action.type);

    (async () => {
      try {
        // 1. Always persist to localStorage regardless of sync state
        await (store.dispatch as AppDispatch)(saveVaultThunk({ vault: nextVault })).unwrap();

        // 2 & 3. Only schedule a cloud sync if the vault has sync enabled AND user is authenticated
        if (isSyncEnabled && isAuthenticated) {
          // Mark the machine dirty (only valid from idle or dirty)
          const machine = syncMachineService.getOrCreate(vaultId);
          if (machine.status === 'idle' || machine.status === 'dirty') {
            machine.markDirty(nextVault.syncState?.lastChangeDate ?? new Date().toISOString());
          }

          // Schedule a debounced sync cycle
          scheduleSync(vaultId, store.dispatch as AppDispatch);
        }
      } catch (error) {
        console.error('[SyncMiddleware] Failed to save vault or schedule sync:', error);
      }
    })();
  }

  return result;
};

